"use client";

import { t } from "@/lib/i18n";
import { usePathname, useRouter } from "next/navigation";
import { crmPagePermissions } from "@/lib/crm-access";
import {
  // Icons untuk CRM
  LayoutDashboard,
  ClipboardList,
  BarChart2,
  TrendingUp,
  Target,
  Users,
  Contact,
  Package,
  Handshake,
  FilePlus,
  List,
  // Icons untuk Services
  Activity,
  Ticket,
  Bell,
  User,
  CheckSquare,
  Briefcase,
  Boxes,
  // Icons untuk Legal
  FileText,
  Shield,
  Database,
  Building2,
  Building,
  ShieldCheck,
  Mail,
  FolderKanban,
  CalendarClock,
  // Icons System/Settings
  History,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Truck,
  Landmark,
  LogOut,
  ShoppingCart,
  Layers,
  HardHat,
  MapPin,
  ClipboardCheck,
  Workflow,
  Settings,
  BarChart3,
  Table2,
  Home,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "@/lib/api"; // <-- PASTIKAN IMPORT INI ADA
import {
  canAccessConnectPath,
  type ConnectAccessContext,
} from "@/lib/connect-access";
import {
  canAccessLegalPath,
  type LegalAccessContext,
} from "@/lib/legal-access";
import { opsPathPermissions } from "@/lib/ops-access";
import {
  hasAnyPermission,
  hasModulePermission,
  type ModuleKey,
} from "@/lib/module-access";
import { canAccessAdmin } from "@/lib/admin-access";
import { useStoredRbacAccess } from "@/lib/use-rbac-access";

type SidebarModuleKey =
  | Extract<
      ModuleKey,
      "crm" | "services" | "scm" | "legal" | "connect" | "ops"
    >
  | "procurement"
  | "insight"
  | "tasks";

type SidebarItem = {
  name: string;
  path?: string;
  icon: LucideIcon;
  isDropdown?: boolean;
  module?: string;
  permission?: string;
  adminOnly?: boolean;
  children?: SidebarItem[];
};

type SidebarGroup = {
  title: string;
  items: SidebarItem[];
};

type SidebarConfig = {
  theme: "light" | "dark";
  activeColor: string;
  hoverColor: string;
  groups: SidebarGroup[];
};

type SidebarCompany = {
  id?: number | string;
  name?: string;
  slug?: string | null;
  code?: string | null;
};

type SidebarProps = {
  isMobile?: boolean;
  onNavigate?: () => void;
};

// --- KONFIGURASI MENU ---
const SIDEBAR_CONFIG: Record<SidebarModuleKey, SidebarConfig> = {
  // 1. MODULE CRM (SALES)
  crm: {
    theme: "light",
    activeColor: "bg-amber-50 text-amber-700 border-r-4 border-amber-600",
    hoverColor: "hover:bg-amber-50 hover:text-amber-700",
    groups: [
      {
        title: "SALES CRM",
        items: [
          { name: "Dashboard", path: "/crm", icon: LayoutDashboard },
          {
            name: "Assignments",
            path: "/crm/assignments",
            icon: ClipboardList,
          },

          { name: "Sales Pipeline", path: "/crm/pipeline", icon: TrendingUp },
          { name: "Analyst Workspace", path: "/crm/analyst-workplace", icon: Target },
          { name: "Opportunities", path: "/crm/opportunities", icon: Target },
          {
            name: "Internal Memo",
            path: "/im",
            icon: ClipboardList,
          },
          {
            name: "Purchase Order",
            path: "/po",
            icon: FileText,
          },
          {
            name: "Sales Orders",
            path: "/crm/sales-orders",
            icon: ShoppingCart,
          },
          { name: "WBS Templates", path: "/crm/wbs-templates", icon: Layers },
          { name: "Customers", path: "/crm/customers", icon: Users },
          { name: "Contact List", path: "/crm/contacts", icon: Contact },
          { name: "Products", path: "/crm/products", icon: Package },
          { name: "Partners", path: "/crm/partners", icon: Handshake },
        ],
      },
      {
        title: "PROCESS FLOW",
        items: [
          { name: "CRM Flow Library", path: "/crm/process-flow", icon: Workflow },
        ],
      },
      {
        title: "USER SETTINGS",
        items: [
          { name: "Log Activity", path: "/crm/activity-logs", icon: History },
        ],
      },
    ],
  },

  // 2. MODULE SERVICES (OPERATIONS)
  services: {
    theme: "light",
    activeColor: "bg-purple-50 text-purple-700 border-r-4 border-purple-600",
    hoverColor: "hover:bg-purple-50 hover:text-purple-700",
    groups: [
      {
        title: "SERVICE OPERATIONS",
        items: [
          { name: "Command Center", path: "/services", icon: Activity },
          { name: "Ticket Lifecycle", path: "/services/tickets", icon: Ticket },
          {
            name: "Notifications",
            path: "/services/notifications",
            icon: Bell,
          },
          {
            name: "Daily Report",
            path: "/services/daily-report",
            icon: ClipboardList,
          },
          {
            name: "Assets Management",
            path: "/services/assets-management",
            icon: Boxes,
          },
          {
            name: "Attendance",
            path: "/services/attendance",
            icon: User,
          },
          {
            name: "Network Inventory",
            path: "/services/assets",
            icon: CheckSquare,
          },
          {
            name: "RCA Analytics",
            path: "/services/case-management",
            icon: Briefcase,
          },
          {
            name: "Audit & Activity",
            path: "/services/tt-progress",
            icon: BarChart2,
          },
        ],
      },
    ],
  },

  // 3. MODULE Legal
  legal: {
    theme: "light",
    activeColor: "bg-green-50 text-green-700 border-r-4 border-green-600",
    hoverColor: "hover:bg-green-50 hover:text-green-700",
    groups: [
      {
        title: "LEGAL & COMPLIANCE",
        items: [
          { name: "Dashboard", path: "/legal", icon: LayoutDashboard },
          { name: "Legal Pipeline", path: "/legal/pipeline", icon: TrendingUp },
          { name: "Documents", path: "/legal/contracts", icon: FileText },
          {
            name: "PDP Form",
            icon: Shield,
            isDropdown: true,
            children: [
              {
                name: "Create Form",
                path: "/legal/pdp/create",
                icon: FilePlus,
              },
              { name: "PDP List", path: "/legal/pdp", icon: List },
            ],
          },
          { name: "PDP Data", path: "/legal/pdp-data", icon: Database },
          { name: "Customers", path: "/legal/customers", icon: Users },
          { name: "Partners", path: "/legal/partners", icon: Handshake },
          { name: "Banks", path: "/legal/banks", icon: Landmark },
          { name: "Vendors", path: "/legal/vendors", icon: Truck },
          { name: "Contact List", path: "/legal/contacts", icon: Contact },
        ],
      },
      {
        title: "WORKSPACE",
        items: [
          {
            name: "Document Submissions",
            path: "/legal/workspaces/submissions",
            icon: FolderKanban,
          },
          {
            name: "Needs Approval",
            path: "/legal/workspaces/approvals",
            icon: CheckSquare,
          },
        ],
      },
      {
        title: "STORAGE BY COMPANY",
        items: [], // <-- DIKOSONGKAN KARENA AKAN DI-GENERATE OTOMATIS OLEH DB
      },
      {
        title: "SETTINGS",
        items: [
          { name: "Companies", path: "/legal/companies", icon: Building },
          { name: "Log Activity", path: "/legal/activity-logs", icon: History },
        ],
      },
    ],
  },

  // 4. MODULE SCM (SUPPLY CHAIN)
  scm: {
    theme: "light",
    activeColor: "bg-orange-50 text-orange-700 border-r-4 border-orange-600",
    hoverColor: "hover:bg-orange-50 hover:text-orange-700",
    groups: [
      {
        title: "SCM",
        items: [
          { name: "Material Stock", path: "/scm/material-stock", icon: Boxes },
          {
            name: "Material Catalog",
            path: "/scm/material-catalogs",
            icon: Package,
          },
        ],
      },
    ],
  },

  procurement: {
    theme: "light",
    activeColor:
      "bg-orange-50 text-orange-700 border-r-4 border-orange-600",
    hoverColor: "hover:bg-orange-50 hover:text-orange-700",
    groups: [
      {
        title: "PROCUREMENT",
        items: [
          {
            name: "Dashboard",
            path: "/procurement",
            icon: LayoutDashboard,
            permission: "procurement.dashboard.view",
          },
          {
            name: "Documents",
            path: "/procurement/documents",
            icon: FileText,
            permission: "procurement.documents.view",
          },
          {
            name: "Vendors",
            path: "/procurement/vendors",
            icon: Truck,
            permission: "procurement.vendors.view",
          },
          {
            name: "Projects",
            path: "/procurement/projects",
            icon: FolderKanban,
            permission: "procurement.projects.view",
          },
        ],
      },
      {
        title: "GOVERNANCE",
        items: [
          {
            name: "Audit Log",
            path: "/procurement/audit-logs",
            icon: History,
            permission: "procurement.audit_logs.view",
          },
          {
            name: "Settings",
            path: "/procurement/settings",
            icon: Settings,
            permission: "procurement.settings.view",
          },
        ],
      },
    ],
  },

  insight: {
    theme: "light",
    activeColor:
      "bg-orange-50 text-orange-700 border-r-4 border-orange-600",
    hoverColor: "hover:bg-orange-50 hover:text-orange-700",
    groups: [
      {
        title: "INSIGHT",
        items: [
          { name: "Home", path: "/insight", icon: Home, module: "insight" },
          { name: "Datasets", path: "/insight/datasets", icon: Table2, permission: "insight.datasets.view" },
          { name: "Reports", path: "/insight/reports", icon: BarChart3, permission: "insight.reports.view" },
        ],
      },
      {
        title: "GOVERNANCE",
        items: [
          { name: "Audit Log", path: "/insight/audit-logs", icon: History, permission: "insight.audit_logs.view" },
          { name: "Settings", path: "/insight/settings", icon: Settings, permission: "insight.settings.view" },
        ],
      },
    ],
  },

  // 5. MODULE TASKS
  tasks: {
    theme: "light",
    activeColor: "bg-violet-50 text-violet-700 border-r-4 border-violet-600",
    hoverColor: "hover:bg-violet-50 hover:text-violet-700",
    groups: [
      {
        title: "MY TASKS",
        items: [{ name: "Task Hub", path: "/tasks", icon: CheckSquare }],
      },
    ],
  },

  // 6. MODULE CONNECT
  connect: {
    theme: "light",
    activeColor:
      "bg-[color:var(--color-accent-soft)] text-[color:var(--color-primary-hover)] border-r-4 border-[color:var(--color-primary)]",
    hoverColor:
      "hover:bg-[color:var(--color-accent-soft)] hover:text-[color:var(--color-primary-hover)]",
    groups: [
      {
        title: "CONNECT OPERATIONS",
        items: [
          { name: "Dashboard", 
            path: "/connect", 
            icon: LayoutDashboard,
            permission: "connect.dashboard.view",
          },
          {
            name: "Non-Project IM",
            path: "/connect/non-project-im",
            icon: FilePlus,
            permission: "im.view",
          },
          {
            name: "Meeting Rooms",
            path: "/connect/meetings",
            icon: CalendarClock,
            permission: "connect.meetings.view",
          },
          { name: "Vehicle Booking", 
            path: "/connect/vehicles",
            icon: Truck,
            permission: "connect.vehicles.view",
          },
          { name: "Visitors", 
            path: "/connect/visitors", 
            icon: Users,
            permission: "connect.visitors.view",
          },
          {
            name: "Booking History",
            path: "/connect/booking-history",
            icon: History,
            permission: "connect.booking_history.view",
          },
          { name: "Reports",
            path: "/connect/reports",
            icon: BarChart2,
            permission: "connect.reports.view",
          },
          { name: "Notifications", 
            path: "/connect/notifications", 
            icon: Bell,
            permission: "connect.notifications.view",
          },
          { name: "GA Assets", 
            path: "/connect/ga-assets", 
            icon: Boxes,
            permission: "connect.ga_assets.view",
          },
          { name: "Absensi TLH gps", 
            path: "/connect/absensi-tlh-gps", 
            icon: MapPin,
            permission: "connect.matta.view",
          },
          { name: "Receptionist Checklist", 
            path: "/connect/receptionist-checklist", 
            icon: ClipboardCheck,
            permission: "connect.ga_assets.view",
          },
        ],
      },
      {
        title: "SYSTEM",
        items: [
          {
            name: "Activity Logs",
            path: "/connect/activity-logs",
            icon: History,
            permission: "connect.activity_logs.view",
          },
        ],
      },
    ],
  },

  // 7. MODULE OPS
  ops: {
    theme: "light",
    activeColor: "bg-red-50 text-red-700 border-r-4 border-red-600",
    hoverColor: "hover:bg-red-50 hover:text-red-700",
    groups: [
      {
        title: "OPERATIONS",
        items: [
          { name: "Dashboard", path: "/ops", icon: LayoutDashboard },
          { name: "Projects", path: "/ops/projects", icon: HardHat },
          { name: "Audit Logs", path: "/ops/audit-logs", icon: History },
        ],
      },
    ],
  },
};

const crmPermissionsForPath = (path: string): readonly string[] => {
  const scopedPage = crmPagePermissions(path);
  if (scopedPage) return scopedPage;
  if (path === "/im" || path.startsWith("/crm/so/im")) {
    return ["im.view"];
  }

  if (path === "/po" || path.startsWith("/crm/so/po")) {
    return ["so.purchase_orders.view"];
  }

  if (path === "/crm/sales-orders" || path.startsWith("/crm/sales-orders/")) {
    return ["so.sales_orders.view"];
  }

  if (path.startsWith("/crm/wbs-templates")) return ["so.wbs_templates.view"];
  if (path.startsWith("/crm/assignments")) return ["crm.assignments.view"];
  if (path.startsWith("/crm/pipeline")) return ["crm.pipeline.view"];
  if (path.startsWith("/crm/opportunities")) return ["crm.opportunities.view"];
  if (path.startsWith("/crm/customers")) return ["crm.customers.view"];
  if (path.startsWith("/crm/contacts")) return ["crm.contacts.view"];
  if (path.startsWith("/crm/products")) return ["crm.products.view"];
  if (path.startsWith("/crm/partners")) return ["crm.partners.view"];
  if (path.startsWith("/crm/activity-logs")) return ["crm.activity_logs.view"];

  return ["crm.dashboard.view"];
};

const servicesPermissionsForPath = (path: string): readonly string[] => {
  if (path.startsWith("/services/tickets")) return ["services.tickets.view"];
  if (path.startsWith("/services/notifications")) return ["services.notifications.view"];
  if (path.startsWith("/services/daily-report")) return ["services.daily_report.view"];
  if (path.startsWith("/services/assets")) return ["services.assets.view"];
  if (path.startsWith("/services/attendance")) return ["services.attendance.view"];
  if (path.startsWith("/services/case-management")) return ["services.case_management.view"];
  if (path.startsWith("/services/tt-progress")) return ["services.flp.view"];

  return ["services.dashboard.view"];
};

const scmPermissionsForPath = (path: string): readonly string[] => {
  if (path.startsWith("/scm/material-catalogs")) return ["scm.material_catalog.view"];
  if (path.startsWith("/scm/material-stock")) return ["scm.material_stock.view"];

  return ["scm.assets.view"];
};

export default function Sidebar({ isMobile = false, onNavigate }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { allowedModules, permissions, roles } = useStoredRbacAccess();

  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  // STATE BARU UNTUK MENYIMPAN DATA COMPANIES DARI API
  const [dbCompanies, setDbCompanies] = useState<SidebarCompany[]>([]);

  useEffect(() => {
    // FUNGSI UNTUK MENGAMBIL DATA COMPANIES
    const fetchCompaniesForSidebar = async () => {
      try {
        const apiBase = process.env.NEXT_PUBLIC_API_URL || "/api";
        const res = await fetchWithAuth(`${apiBase}/companies`);

        if (res.ok) {
          const data = await res.json();
          const companyList = (Array.isArray(data) ? data : data?.data || [])
            .filter(
              (company: unknown): company is SidebarCompany =>
                Boolean(company) &&
                typeof company === "object" &&
                !Array.isArray(company),
            );
          setDbCompanies(companyList);
        }
      } catch (error) {
        console.error("Gagal load company untuk sidebar:", error);
      }
    };

    fetchCompaniesForSidebar();
  }, [pathname]);

  const toggleDropdown = (name: string) => {
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleLogout = () => {
    // Redirect sidebar logout button to /tasks instead of performing logout
    router.push("/tasks");
    onNavigate?.();
  };

  const navigateTo = (path: string) => {
    router.push(path);
    onNavigate?.();
  };

  const isServicesPath = pathname?.startsWith("/services");
  const isScmPath = pathname?.startsWith("/scm");
  const isLegalPath = pathname?.startsWith("/legal");
  const isConnectPath = pathname?.startsWith("/connect");
  const isTasksPath = pathname?.startsWith("/tasks");
  const isOpsPath = pathname?.startsWith("/ops");
  const isProcurementPath = pathname?.startsWith("/procurement");
  const isInsightPath = pathname?.startsWith("/insight");

  let currentModule: SidebarModuleKey = "crm";
  if (isServicesPath) currentModule = "services";
  else if (isScmPath) currentModule = "scm";
  else if (isLegalPath) currentModule = "legal";
  else if (isConnectPath) currentModule = "connect";
  else if (isTasksPath) currentModule = "tasks";
  else if (isOpsPath) currentModule = "ops";
  else if (isProcurementPath) currentModule = "procurement";
  else if (isInsightPath) currentModule = "insight";

  const config = SIDEBAR_CONFIG[currentModule];
  const isDark = config.theme === "dark";
  const isSidebarCollapsed = !isMobile && isCollapsed;

  const filteredGroups = useMemo(() => {
    const connectContext: ConnectAccessContext = { allowedModules, permissions };
    const legalContext: LegalAccessContext = { allowedModules, permissions };

    const groupsWithDynamicItems = config.groups.map((group) => {
      if (group.title === "STORAGE BY COMPANY") {
        return {
          ...group,
          items: dbCompanies.map((company) => {
            const companySlug =
              company.slug ||
              company.code?.toLowerCase() ||
              String(company.id || "");
            const companyName = company.name || companySlug || "Company";
            return {
              name: companyName,
              icon: Building2, // Icon dipukul rata pakai gedung
              isDropdown: true,
              children: [
                {
                  name: t("Perjanjian"),
                  path: `/legal/${companySlug}/contracts`,
                  icon: FileText,
                },
                {
                  name: t("Legalitas"),
                  path: `/legal/${companySlug}/legality`,
                  icon: ShieldCheck,
                },
                {
                  name: t("Surat"),
                  path: `/legal/${companySlug}/letters`,
                  icon: Mail,
                },
              ],
            };
          }),
        };
      }

      return group;
    });

    const canShowPath = (path: string): boolean => {
      if (currentModule === "connect") {
        return canAccessConnectPath(path, connectContext);
      }

      if (currentModule === "legal") {
        return canAccessLegalPath(path, legalContext);
      }

      if (currentModule === "crm") {
        return hasAnyPermission(permissions, crmPermissionsForPath(path));
      }

      if (currentModule === "ops") {
        return hasAnyPermission(permissions, opsPathPermissions(path));
      }

      if (currentModule === "services") {
        return hasAnyPermission(permissions, servicesPermissionsForPath(path));
      }

      if (currentModule === "scm") {
        return hasAnyPermission(permissions, scmPermissionsForPath(path));
      }

      if (currentModule === "tasks") {
        return hasAnyPermission(permissions, ["tasks.inbox.view", "tasks.view"]);
      }

      return hasModulePermission(currentModule as ModuleKey, permissions);
    };

    const canShowItem = (item: SidebarItem): boolean => {
      if (item.adminOnly && !roles.includes("super_admin")) {
        return false;
      }

      if (item.permission) {
        const hasPermission = hasAnyPermission(
          permissions,
          [item.permission]
        );

        if (currentModule === "connect" && item.path) {
          return hasPermission && canAccessConnectPath(item.path, connectContext);
        }

        return hasPermission;
      }

      if (item.module) {
        return hasModulePermission(
          item.module as ModuleKey,
          permissions
        );
      }

      if (item.path) {
        return canShowPath(item.path);
      }

      return true;
    };

    const filterItem = (item: SidebarItem): SidebarItem | null => {
      if (item.children) {
        const children = item.children
          .map(filterItem)
          .filter((child): child is SidebarItem => Boolean(child));

        return children.length > 0 ? { ...item, children } : null;
      }

      if (!item.path) {
        return item;
      }

      return canShowItem(item) ? item : null;
    };

    const baseGroups = groupsWithDynamicItems
      .map((group) => ({
        ...group,
        items: group.items
          .map(filterItem)
          .filter((item): item is SidebarItem => Boolean(item)),
      }))
      .filter((group) => group.items.length > 0);

    // Inject Admin Center for admins
    if (canAccessAdmin(permissions)) {
      baseGroups.push({
        title: t("ADMINISTRATION"),
        items: [
          {
            name: t("Admin Center"),
            path: "/admin",
            icon: Shield,
          },
        ],
      });
    }

    return baseGroups;
  }, [allowedModules, config.groups, currentModule, dbCompanies, permissions, roles]);

  return (
    <motion.aside
      initial={false}
      animate={{ width: isSidebarCollapsed ? 80 : isMobile ? "100%" : 260 }}
      className={`relative h-full min-h-0 flex flex-col border-r transition-colors duration-500 z-40 ${
        isDark
          ? "bg-slate-900 border-slate-800 text-slate-300"
          : "bg-white border-gray-200 text-gray-600"
      }`}
    >
      {/* A. Collapse Toggle */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isSidebarCollapsed ? t("Expand sidebar") : t("Collapse sidebar")}
        className={`absolute -right-3 top-6 p-1 rounded-full border shadow-sm z-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
          isMobile ? "hidden" : ""
        } ${
          isDark
            ? "bg-slate-800 border-slate-700 text-white"
            : "bg-white border-gray-200 text-gray-600"
        }`}
      >
        {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* B. Module Title (Visual Context) */}
      <div
        className={`pt-6 pb-2 px-6 flex items-center gap-3 ${isSidebarCollapsed ? "justify-center" : ""}`}
      >
        {!isSidebarCollapsed && (
          <div>
            <h2
              className={`text-xs font-bold uppercase tracking-wider opacity-50 mb-1`}
            >
              {t("Module")}
            </h2>
            <h1
              className={`text-lg font-extrabold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {currentModule.toUpperCase()}
            </h1>
          </div>
        )}
      </div>

      {/* C. Dynamic Menu Items (Grouped) */}
      <nav className="flex-1 px-3 space-y-6 overflow-y-auto py-4 custom-scrollbar">
        {filteredGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            {/* Group Title */}
            {!isSidebarCollapsed && (
              <h3
                className={`px-3 mb-2 text-xs font-semibold uppercase tracking-wider ${isDark ? "text-slate-500" : "text-gray-400"}`}
              >
                {t(group.title)}
              </h3>
            )}

            {/* Menu Items */}
            <div className="space-y-1">
              {group.items.map((item) => {
                const isActive = pathname === item.path;
                const Icon = item.icon;
                const isMenuOpen = openMenus[item.name];
                const children = item.children || [];

                // LOGIC DROPDOWN (Jika isDropdown true)
                if (item.isDropdown && !isSidebarCollapsed) {
                  return (
                    <div key={item.name} className="space-y-1">
                      <button
                        type="button"
                        onClick={() => toggleDropdown(item.name)}
                        aria-expanded={Boolean(isMenuOpen)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 font-medium text-sm ${
                          isMenuOpen
                            ? "text-teal-700 bg-teal-50/50"
                            : "hover:bg-gray-50 text-gray-600"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={18} className="opacity-70" />
                          <span>{t(item.name)}</span>
                        </div>
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${isMenuOpen ? "rotate-180" : ""}`}
                        />
                      </button>

                      <AnimatePresence>
                        {isMenuOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden ml-4 border-l-2 border-gray-100 space-y-1 mt-1"
                          >
                            {children.map((child) => {
                              const childPath = child.path;
                              if (!childPath) {
                                return null;
                              }

                              const isChildActive = pathname === childPath;
                              return (
                                <div
                                  key={childPath}
                                  onClick={() => navigateTo(childPath)}
                                  className={`flex items-center gap-3 px-4 py-2 text-xs font-medium cursor-pointer transition-colors ${
                                    isChildActive
                                      ? "text-teal-700 font-bold bg-teal-50/30 rounded-r-lg"
                                      : "text-gray-500 hover:text-teal-600"
                                  }`}
                                >
                                  <child.icon size={14} />
                                  <span>{t(child.name)}</span>
                                </div>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                // LOGIC MENU BIASA (Tidak punya dropdown)
                return (
                  <div
                    key={item.path || item.name}
                    onClick={() => item.path && navigateTo(item.path)}
                    onKeyDown={(event) => {
                      if (item.path && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        navigateTo(item.path);
                      }
                    }}
                    role="link"
                    tabIndex={0}
                    className={`
                      group relative flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 font-medium text-sm
                      ${isActive ? config.activeColor : config.hoverColor}
                      ${isSidebarCollapsed ? "justify-center" : ""}
                    `}
                  >
                    <Icon size={18} className={isActive ? "" : "opacity-70"} />
                    {!isSidebarCollapsed && <span>{t(item.name)}</span>}

                    {/* Tooltip for Collapsed Mode */}
                    {isSidebarCollapsed && (
                      <div
                        className={`absolute left-16 px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 ${
                          isDark
                            ? "bg-white text-slate-900"
                            : "bg-gray-900 text-white"
                        }`}
                      >
                        {t(item.name)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* D. Footer */}
      <div className={`py-4 border-t ${isDark ? "border-slate-800" : "border-gray-100"}`}>
        <div className="relative w-full flex justify-center px-4">
          <div className="group relative w-full flex justify-center">
            <button
              onClick={handleLogout}
              aria-label={isSidebarCollapsed ? t("Back to MyTasks") : undefined}
              className={`flex items-center gap-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isSidebarCollapsed
                  ? "w-14 h-14 p-0 rounded-2xl bg-gradient-to-tr from-[color:var(--color-primary)] to-[color:var(--color-text-secondary)] text-white flex items-center justify-center shadow-md hover:brightness-105"
                  : `w-full px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md bg-gradient-to-r from-[color:var(--color-primary)] to-[color:var(--color-primary-hover)] hover:brightness-105 transform hover:-translate-y-0.5`
              }`}
            >
              <LogOut size={20} />
              {!isSidebarCollapsed && <span>{t("Back to MyTasks")}</span>}
            </button>

            {isSidebarCollapsed && (
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-900 text-white opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity">
                {t("Back to MyTasks")}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
